import React from 'react';
import { Row, Col, Card, CardBody, Badge, Spinner } from 'reactstrap';
import { Icon } from '../../../../components/Component'; // Assuming this path is correct
import { formatCurrency } from '../../../../utils/Utils'; // Assuming this path is correct

/**
 * Formats time in milliseconds to a human-readable format
 * @param {number} milliseconds - Time in milliseconds
 * @returns {string} - Formatted time string
 */
const formatTimeToFirstAction = (milliseconds) => {
  if (milliseconds === undefined || milliseconds === null || isNaN(milliseconds)) return 'N/A';
  if (milliseconds === 0) return '0 minutes'; // Handle 0 explicitly if needed

  // Convert to hours
  const hours = milliseconds / (1000 * 60 * 60);

  if (hours < 1) {
    // Less than an hour, show minutes
    const minutes = Math.round(hours * 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  } else if (hours < 24) {
    // Less than a day, show hours
    const roundedHours = Math.round(hours * 10) / 10; // One decimal place
    return `${roundedHours} hour${roundedHours !== 1 ? 's' : ''}`;
  } else {
    // More than a day, show days
    const days = Math.round(hours / 24 * 10) / 10; // One decimal place
    return `${days} day${days !== 1 ? 's' : ''}`;
  }
};


/**
 * Formats duration in milliseconds to "Xm Ys" format.
 * @param {number} milliseconds - Duration in milliseconds
 * @returns {string} - Formatted duration string
 */
const formatDurationMinutesSeconds = (milliseconds) => {
  if (milliseconds === undefined || milliseconds === null || isNaN(milliseconds)) {
    return 'N/A';
  }
  if (milliseconds === 0) {
    return '0s';
  }

  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes > 0 && seconds > 0) {
    return `${minutes}m ${seconds}s`;
  } else if (minutes > 0) { // seconds must be 0 here
    return `${minutes}m`;
  } else if (seconds > 0) { // minutes must be 0 here
    return `${seconds}s`;
  } else { // totalSeconds is 0 (e.g. milliseconds < 1000 and > 0)
    return '0s';
  }
};

const PerformanceMetrics = ({ data = {}, agentPerformance = {}, hotLeadFunnel = {}, isLoading = false }) => {
  const iconContainerStyle = { width: '40px', height: '40px', fontSize: '1.1rem' }; // Centralized style for icon container

  // Calculate Key Efficiency Ratios
  const activitiesPerDeal = (agentPerformance?.dealsCompleted && agentPerformance.dealsCompleted > 0)
    ? ((agentPerformance.activitiesCompleted || 0) / agentPerformance.dealsCompleted).toFixed(1)
    : 'N/A';

  const hotLeadsAssignedPerWin = (hotLeadFunnel?.hotLeadsWon && hotLeadFunnel.hotLeadsWon > 0)
    ? ((hotLeadFunnel.hotLeadsAssigned || 0) / hotLeadFunnel.hotLeadsWon).toFixed(1)
    : 'N/A';

  const avgCommissionPerDeal = (agentPerformance?.dealsCompleted && agentPerformance.dealsCompleted > 0 && agentPerformance?.commissionValue)
    ? (agentPerformance.commissionValue / agentPerformance.dealsCompleted).toLocaleString(undefined, { style: 'currency', currency: 'AED', minimumFractionDigits: 0 })
    : 'N/A';

  return (
    <Row className="g-3 mb-4"> {/* Added mb-4 for spacing below the metrics row */}
      {/* Original Performance Metrics */}
      <Col sm="6" lg="3">
        <Card className="h-100 shadow-sm border-0">
          <CardBody className="p-3 p-lg-4">
            <div className="d-flex align-items-center mb-3">
              <div
                className="rounded-circle bg-primary bg-opacity-10 me-3 d-flex align-items-center justify-content-center flex-shrink-0"
                style={iconContainerStyle}
              >
                <Icon name="briefcase" className="text-primary" />
              </div>
              <h6 className="mb-0 fw-semibold">Deals Closed</h6>
              {!isLoading && data.rankDealsClosedCount && <Badge color="light" text="dark" pill className="ms-auto border">#{data.rankDealsClosedCount}</Badge>}
            </div>
            {isLoading ? (
              <div className="d-flex justify-content-center py-3">
                <Spinner size="sm" color="primary" />
              </div>
            ) : (
              <>
                <div className="d-flex align-items-baseline">
                  <h3 className="mb-0 me-2">{data.dealsCompleted || 0}</h3>
                  {/* TODO: Replace with dynamic data for percentage change */}
                  {/* Example: <span className={data.dealsChangePercentage > 0 ? 'text-success small' : 'text-danger small'}><Icon name={data.dealsChangePercentage > 0 ? 'arrow-up' : 'arrow-down'} /> {Math.abs(data.dealsChangePercentage)}%</span> */}
                  <span className="text-success small"><Icon name="arrow-up" /> 12.4%</span>
                </div>
                <p className="text-muted small mb-0 mt-1">Value: {formatCurrency(data.dealValue)}</p>
              </>
            )}
          </CardBody>
        </Card>
      </Col>

      <Col sm="6" lg="3">
        <Card className="h-100 shadow-sm border-0">
          <CardBody className="p-3 p-lg-4">
            <div className="d-flex align-items-center mb-3">
              <div
                className="rounded-circle bg-danger bg-opacity-10 me-3 d-flex align-items-center justify-content-center flex-shrink-0"
                style={iconContainerStyle}
              >
                <Icon name="home" className="text-danger" />
              </div>
              <h6 className="mb-0 fw-semibold">Listings Acquired</h6>
              {!isLoading && data.rankListingsAcquiredCount && <Badge color="light" text="dark" pill className="ms-auto border">#{data.rankListingsAcquiredCount}</Badge>}
            </div>
            {isLoading ? (
              <div className="d-flex justify-content-center py-3">
                <Spinner size="sm" color="danger" />
              </div>
            ) : (
              <>
                <div className="d-flex align-items-baseline">
                  <h3 className="mb-0 me-2">{data.listingsAcquired || 0}</h3>
                  {/* TODO: Replace with dynamic data */}
                  <span className="text-success small"><Icon name="arrow-up" /> 8.2%</span>
                </div>
                <p className="text-muted small mb-0 mt-1">Value: {formatCurrency(data.listingsAcquiredValue)}</p>
              </>
            )}
          </CardBody>
        </Card>
      </Col>

      <Col sm="6" lg="3">
        <Card className="h-100 shadow-sm border-0">
          <CardBody className="p-3 p-lg-4">
            <div className="d-flex align-items-center mb-3">
              <div
                className="rounded-circle bg-info bg-opacity-10 me-3 d-flex align-items-center justify-content-center flex-shrink-0"
                style={iconContainerStyle}
              >
                <Icon name="users" className="text-info" />
              </div>
              <h6 className="mb-0 fw-semibold">First Actions</h6>
              {!isLoading && data.rankLeadConversionsCount && <Badge color="light" text="dark" pill className="ms-auto border">#{data.rankLeadConversionsCount}</Badge>}
            </div>
            {isLoading ? (
              <div className="d-flex justify-content-center py-3">
                <Spinner size="sm" color="info" />
              </div>
            ) : (
              <>
                <div className="d-flex align-items-baseline">
                  <h3 className="mb-0 me-2">{data.firstActionCount || 0}</h3>
                  {/* TODO: Replace with dynamic data */}
                  <span className="text-success small"><Icon name="arrow-up" /> 15.7%</span>
                </div>
                <p className="text-muted small mb-0 mt-1">Avg Time: {formatTimeToFirstAction(data.avgTimeToFirstAction)}</p>
              </>
            )}
          </CardBody>
        </Card>
      </Col>

      <Col sm="6" lg="3">
        <Card className="h-100 shadow-sm border-0">
          <CardBody className="p-3 p-lg-4">
            <div className="d-flex align-items-center mb-3">
              <div
                className="rounded-circle bg-success bg-opacity-10 me-3 d-flex align-items-center justify-content-center flex-shrink-0"
                style={iconContainerStyle}
              >
                <Icon name="check-square" className="text-success" />
              </div>
              <h6 className="mb-0 fw-semibold">Tasks</h6>
              {!isLoading && data.rankActivitiesCompletedCount && <Badge color="light" text="dark" pill className="ms-auto border">#{data.rankActivitiesCompletedCount}</Badge>}
            </div>
            {isLoading ? (
              <div className="d-flex justify-content-center py-3">
                <Spinner size="sm" color="success" />
              </div>
            ) : (
              <>
                <div className="d-flex align-items-baseline">
                  <h3 className="mb-0 me-2">{data.activitiesCompleted || 0}</h3>
                  {/* TODO: Replace with dynamic data for percentage change */}
                  <span className="text-danger small"><Icon name="arrow-down" /> 5.2%</span>
                </div>
                <p className="text-muted small mb-0 mt-1">Completion: {(data.activityCompletionRate !== undefined ? data.activityCompletionRate + '%' : 'N/A')}</p>
              </>
            )}
          </CardBody>
        </Card>
      </Col>

      {/* Key Efficiency Ratios - styled as Performance Metrics */}
      <Col sm="6" lg="3">
        <Card className="h-100 shadow-sm border-0">
          <CardBody className="p-3 p-lg-4">
            <div className="d-flex align-items-center mb-3">
              <div
                className="rounded-circle bg-warning bg-opacity-10 me-3 d-flex align-items-center justify-content-center flex-shrink-0"
                style={iconContainerStyle}
              >
                <Icon name="list-check" className="text-warning" />
              </div>
              <h6 className="mb-0 fw-semibold">Activities per Deal</h6>
            </div>
            {isLoading ? (
              <div className="d-flex justify-content-center py-3">
                <Spinner size="sm" color="warning" />
              </div>
            ) : (
              <h3 className="mb-0">{activitiesPerDeal}</h3>
            )}
          </CardBody>
        </Card>
      </Col>

      <Col sm="6" lg="3">
        <Card className="h-100 shadow-sm border-0">
          <CardBody className="p-3 p-lg-4">
            <div className="d-flex align-items-center mb-3">
              <div
                className="rounded-circle bg-secondary bg-opacity-10 me-3 d-flex align-items-center justify-content-center flex-shrink-0" // Changed color
                style={iconContainerStyle}
              >
                <Icon name="users" className="text-secondary" /> {/* Changed color */}
              </div>
              <h6 className="mb-0 fw-semibold">Hot Leads Assigned per Win</h6>
            </div>
            {isLoading ? (
              <div className="d-flex justify-content-center py-3">
                <Spinner size="sm" color="secondary" />
              </div>
            ) : (
              <h3 className="mb-0">{hotLeadsAssignedPerWin}</h3>
            )}
          </CardBody>
        </Card>
      </Col>

      <Col sm="6" lg="3">
        <Card className="h-100 shadow-sm border-0">
          <CardBody className="p-3 p-lg-4">
            <div className="d-flex align-items-center mb-3">
              <div
                className="rounded-circle bg-dark bg-opacity-10 me-3 d-flex align-items-center justify-content-center flex-shrink-0" // Changed color
                style={iconContainerStyle}
              >
                <Icon name="money" className="text-dark" /> {/* Changed color */}
              </div>
              <h6 className="mb-0 fw-semibold">Avg. Commission / Deal</h6>
            </div>
            {isLoading ? (
              <div className="d-flex justify-content-center py-3">
                <Spinner size="sm" color="dark" />
              </div>
            ) : (
              <h3 className="mb-0">{avgCommissionPerDeal}</h3>
            )}
          </CardBody>
        </Card>
      </Col>

      {/* Pocket Listing Count */}
      <Col sm="6" lg="3">
        <Card className="h-100 shadow-sm border-0">
          <CardBody className="p-3 p-lg-4">
            <div className="d-flex align-items-center mb-3">
              <div
                className="rounded-circle bg-dark bg-opacity-10 me-3 d-flex align-items-center justify-content-center flex-shrink-0"
                style={iconContainerStyle}
              >
                <Icon name="archive" className="text-dark" />
              </div>
              <h6 className="mb-0 fw-semibold">Pocket Listings</h6>
              {/* Optional: Add rank if available: !isLoading && data.rankPocketListingCount && <Badge color="light" text="dark" pill className="ms-auto border">#{data.rankPocketListingCount}</Badge> */}
            </div>
            {isLoading ? (
              <div className="d-flex justify-content-center py-3">
                <Spinner size="sm" color="dark" />
              </div>
            ) : (
              <h3 className="mb-0">{data.pocketListingCount || 0}</h3>
            )}
          </CardBody>
        </Card>
      </Col>

      {/* Cold Calls Made */}
      <Col sm="6" lg="3">
        <Card className="h-100 shadow-sm border-0">
          <CardBody className="p-3 p-lg-4">
            <div className="d-flex align-items-center mb-3">
              <div
                className="rounded-circle bg-primary bg-opacity-10 me-3 d-flex align-items-center justify-content-center flex-shrink-0"
                style={iconContainerStyle}
              >
                <Icon name="call" className="text-primary" />
              </div>
              <h6 className="mb-0 fw-semibold">Cold Calls Made</h6>
            </div>
            {isLoading ? (
              <div className="d-flex justify-content-center py-3">
                <Spinner size="sm" color="primary" />
              </div>
            ) : (
              <h3 className="mb-0">{data.coldCallsMade || 0}</h3>
            )}
          </CardBody>
        </Card>
      </Col>

      {/* Cold Calls Answered */}
      <Col sm="6" lg="3">
        <Card className="h-100 shadow-sm border-0">
          <CardBody className="p-3 p-lg-4">
            <div className="d-flex align-items-center mb-3">
              <div
                className="rounded-circle bg-success bg-opacity-10 me-3 d-flex align-items-center justify-content-center flex-shrink-0"
                style={iconContainerStyle}
              >
                <Icon name="phone-call" className="text-success" /> {/* Assuming 'phone-call' icon exists */}
              </div>
              <h6 className="mb-0 fw-semibold">Cold Calls Answered</h6>
            </div>
            {isLoading ? (
              <div className="d-flex justify-content-center py-3">
                <Spinner size="sm" color="success" />
              </div>
            ) : (
              <h3 className="mb-0">{data.coldCallsAnswered || 0}</h3>
              // Optional: Display answer rate
              // {data.coldCallsMade > 0 && <p className="text-muted small mb-0 mt-1">Rate: {((data.coldCallsAnswered / data.coldCallsMade) * 100).toFixed(1)}%</p>}
            )}
          </CardBody>
        </Card>
      </Col>

      {/* Total Cold Call Duration */}
      <Col sm="6" lg="3">
        <Card className="h-100 shadow-sm border-0">
          <CardBody className="p-3 p-lg-4">
            <div className="d-flex align-items-center mb-3">
              <div
                className="rounded-circle bg-warning bg-opacity-10 me-3 d-flex align-items-center justify-content-center flex-shrink-0"
                style={iconContainerStyle}
              >
                <Icon name="clock" className="text-warning" />
              </div>
              <h6 className="mb-0 fw-semibold">Total Cold Call Time</h6>
            </div>
            {isLoading ? (
              <div className="d-flex justify-content-center py-3">
                <Spinner size="sm" color="warning" />
              </div>
            ) : (
              <h3 className="mb-0">{formatDurationMinutesSeconds(data.totalColdCallDuration)}</h3>
            )}
          </CardBody>
        </Card>
      </Col>

      {/* Average Cold Call Duration */}
      <Col sm="6" lg="3">
        <Card className="h-100 shadow-sm border-0">
          <CardBody className="p-3 p-lg-4">
            <div className="d-flex align-items-center mb-3">
              <div
                className="rounded-circle bg-info bg-opacity-10 me-3 d-flex align-items-center justify-content-center flex-shrink-0"
                style={iconContainerStyle}
              >
                <Icon name="activity" className="text-info" />
              </div>
              <h6 className="mb-0 fw-semibold">Avg. Cold Call Time</h6>
            </div>
            {isLoading ? (
              <div className="d-flex justify-content-center py-3">
                <Spinner size="sm" color="info" />
              </div>
            ) : (
              <h3 className="mb-0">{formatDurationMinutesSeconds(data.avgColdCallDuration)}</h3>
            )}
          </CardBody>
        </Card>
      </Col>

    </Row>
  );
};

export default PerformanceMetrics;
